<script setup lang="ts">
import { computed, nextTick, onUnmounted, reactive, ref, useTemplateRef, watch } from "vue"

import { ICON } from "@/lib/icons"
import type { ImageSummary } from "@/types/models"

const props = defineProps<{
  open: boolean
  images: ImageSummary[]
  initialIndex: number
  alt: string
}>()

const emit = defineEmits<{
  "update:open": [value: boolean]
}>()

const closeButton = useTemplateRef<{ $el?: HTMLElement }>("closeButton")

const MIN_SCALE = 1
const MAX_SCALE = 5
const WHEEL_STEP = 0.25

const currentIndex = ref(0)
const scale = ref(MIN_SCALE)
const pan = reactive({ x: 0, y: 0 })
const pointers = new Map<number, Point>()

let previousBodyOverflow: string | null = null
let previouslyFocused: HTMLElement | null = null
let lastPanPointer: Point | null = null
let pinchStart: PinchState | null = null

const currentImage = computed(() => props.images[currentIndex.value])
const currentSource = computed(
  () =>
    currentImage.value?.zoom_url ??
    currentImage.value?.large_url ??
    currentImage.value?.thumb_url ??
    undefined,
)
const imageTransform = computed(() => `translate(${pan.x}px, ${pan.y}px) scale(${scale.value})`)

interface Point {
  x: number
  y: number
}

interface PinchState {
  distance: number
  midpoint: Point
  scale: number
  pan: Point
}

function normalizedIndex(index: number): number {
  if (props.images.length === 0) return 0
  return ((index % props.images.length) + props.images.length) % props.images.length
}

function resetTransform() {
  scale.value = MIN_SCALE
  pan.x = 0
  pan.y = 0
  pointers.clear()
  lastPanPointer = null
  pinchStart = null
}

function show(index: number) {
  currentIndex.value = normalizedIndex(index)
  resetTransform()
}

function previous() {
  show(currentIndex.value - 1)
}

function next() {
  show(currentIndex.value + 1)
}

function close() {
  resetTransform()
  emit("update:open", false)
}

function clampScale(value: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, value))
}

function onWheel(event: WheelEvent) {
  const nextScale = clampScale(scale.value + (event.deltaY < 0 ? WHEEL_STEP : -WHEEL_STEP))
  if (nextScale === scale.value) return

  const stage = event.currentTarget as HTMLElement
  const bounds = stage.getBoundingClientRect()
  const pointer = {
    x: event.clientX - (bounds.left + bounds.width / 2),
    y: event.clientY - (bounds.top + bounds.height / 2),
  }
  const ratio = nextScale / scale.value
  pan.x = pointer.x - (pointer.x - pan.x) * ratio
  pan.y = pointer.y - (pointer.y - pan.y) * ratio
  scale.value = nextScale

  if (nextScale === MIN_SCALE) {
    pan.x = 0
    pan.y = 0
  }
}

function eventPoint(event: PointerEvent): Point {
  return { x: event.clientX, y: event.clientY }
}

function pointerPair(): [Point, Point] | null {
  const active = Array.from(pointers.values())
  return active.length === 2 ? [active[0]!, active[1]!] : null
}

function distance([first, second]: [Point, Point]): number {
  return Math.hypot(second.x - first.x, second.y - first.y)
}

function midpoint([first, second]: [Point, Point]): Point {
  return { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 }
}

function beginPinch() {
  const pair = pointerPair()
  if (!pair) return
  pinchStart = {
    distance: Math.max(distance(pair), 1),
    midpoint: midpoint(pair),
    scale: scale.value,
    pan: { ...pan },
  }
  lastPanPointer = null
}

function onPointerDown(event: PointerEvent) {
  const point = eventPoint(event)
  pointers.set(event.pointerId, point)
  const stage = event.currentTarget as HTMLElement
  stage.setPointerCapture?.(event.pointerId)

  if (pointers.size === 1) lastPanPointer = point
  if (pointers.size === 2) beginPinch()
}

function onPointerMove(event: PointerEvent) {
  const priorPoint = pointers.get(event.pointerId)
  if (!priorPoint) return

  const point = eventPoint(event)
  pointers.set(event.pointerId, point)

  if (pointers.size === 1) {
    if (scale.value > MIN_SCALE && lastPanPointer) {
      pan.x += point.x - lastPanPointer.x
      pan.y += point.y - lastPanPointer.y
    }
    lastPanPointer = point
    return
  }

  const pair = pointerPair()
  if (!pair || !pinchStart) return
  const nextMidpoint = midpoint(pair)
  scale.value = clampScale(pinchStart.scale * (distance(pair) / pinchStart.distance))
  pan.x = pinchStart.pan.x + nextMidpoint.x - pinchStart.midpoint.x
  pan.y = pinchStart.pan.y + nextMidpoint.y - pinchStart.midpoint.y
  if (scale.value === MIN_SCALE) {
    pan.x = 0
    pan.y = 0
  }
}

function onPointerEnd(event: PointerEvent) {
  pointers.delete(event.pointerId)
  const stage = event.currentTarget as HTMLElement
  if (stage.hasPointerCapture?.(event.pointerId)) stage.releasePointerCapture(event.pointerId)
  pinchStart = null

  if (pointers.size === 1) {
    lastPanPointer = Array.from(pointers.values())[0]!
  } else {
    lastPanPointer = null
  }
}

function focusableControls(): HTMLElement[] {
  return Array.from(
    document.querySelectorAll<HTMLElement>("[data-testid='photo-lightbox'] button:not([disabled])"),
  )
}

function keepFocusInside(event: KeyboardEvent) {
  const controls = focusableControls()
  if (controls.length === 0) return

  const first = controls[0]!
  const last = controls[controls.length - 1]!
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") close()
  if (event.key === "ArrowLeft") previous()
  if (event.key === "ArrowRight") next()
  if (event.key === "Tab") keepFocusInside(event)
}

async function activate() {
  show(props.initialIndex)
  previouslyFocused = document.activeElement as HTMLElement | null
  previousBodyOverflow = document.body.style.overflow
  document.body.style.overflow = "hidden"
  document.addEventListener("keydown", onKeydown)
  await nextTick()
  closeButton.value?.$el?.focus()
}

function deactivate() {
  document.removeEventListener("keydown", onKeydown)
  if (previousBodyOverflow !== null) {
    document.body.style.overflow = previousBodyOverflow
    previousBodyOverflow = null
  }
  previouslyFocused?.focus()
  previouslyFocused = null
  resetTransform()
}

watch(
  () => props.open,
  (open, wasOpen) => {
    if (open && !wasOpen) void activate()
    if (!open && wasOpen) deactivate()
  },
  { immediate: true },
)

watch(
  () => props.initialIndex,
  (index) => {
    if (props.open) show(index)
  },
)

onUnmounted(deactivate)
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200 ease-out motion-reduce:transition-none"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in motion-reduce:transition-none"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open && currentImage"
        data-testid="photo-lightbox"
        role="dialog"
        aria-modal="true"
        :aria-label="`${alt} photo viewer`"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
        @click.self="close"
      >
        <UButton
          ref="closeButton"
          :icon="ICON.close"
          aria-label="Close photo viewer"
          color="neutral"
          variant="ghost"
          size="xl"
          square
          class="absolute z-20 text-white"
          :style="{
            top: 'max(1rem, var(--safe-top))',
            right: 'max(1rem, var(--safe-right))',
          }"
          @click="close"
        />

        <UButton
          v-if="images.length > 1"
          :icon="ICON.previous"
          aria-label="Previous photo"
          color="neutral"
          variant="ghost"
          size="xl"
          square
          class="absolute left-2 z-20 text-white sm:left-4"
          @click="previous"
        />

        <div
          data-testid="lightbox-stage"
          class="flex size-full touch-none items-center justify-center overflow-hidden p-4 select-none sm:p-16"
          @click.self="close"
          @wheel.prevent="onWheel"
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="onPointerEnd"
          @pointercancel="onPointerEnd"
        >
          <img
            data-testid="lightbox-photo"
            :src="currentSource"
            :alt="`${alt}, photo ${currentIndex + 1} of ${images.length}`"
            draggable="false"
            class="max-h-full max-w-full object-contain select-none"
            :style="{ transform: imageTransform }"
          />
        </div>

        <UButton
          v-if="images.length > 1"
          :icon="ICON.next"
          aria-label="Next photo"
          color="neutral"
          variant="ghost"
          size="xl"
          square
          class="absolute right-2 z-20 text-white sm:right-4"
          @click="next"
        />

        <div
          class="pointer-events-none absolute bottom-0 left-0 z-10 w-full pb-4 text-center text-sm text-white/70"
          :style="{ paddingBottom: 'max(1rem, var(--safe-bottom))' }"
          aria-live="polite"
        >
          {{ currentIndex + 1 }} / {{ images.length }}
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
